// noinspection TypeScriptFieldCanBeMadeReadonly
import { fabric } from 'fabric'
import { Pallet, BoxPosition, Box, ProgramSettings, Sequence } from '../../generated/model/models'
import * as consts from './pattern-editor-constants'
import { FigureBuilder } from './figureBuilder'
import { getPalletDef, PalletDef } from './palletDef'
import { BoxDef } from './boxDef'
import { Snap } from './snap'
import { View } from './view'
import { Generator } from './generator'
import { Image } from 'fabric/fabric-impl'
import { getRobotMovementNumber } from '@/libs/pattern/layerSort'
import { BoxGroupData } from '@/generated/model/boxGroupData'
import * as utils from '@/libs/pattern/utils'
import { calculateCustomLabelPosition, findSequenceByBoxId, getCustomInfeedRotation } from '@/libs/pattern/sequenceUtils'
import LabelPositionEnum = ProgramSettings.LabelPositionEnum

interface PatternCanvasOptions {
  shadow: boolean,
  snapRulers: boolean,
  side: string,
  mode: string,
  debugBoxIds?: boolean
}

const defaultOptions = {
  shadow: true,
  snapRulers: true,
  side: 'L',
  mode: 'full',
  debugBoxIds: false
}

export class PatternCanvas implements View {
  private _palletTopOffset: number = 115
  private canvas: fabric.Canvas
  private boxId = new Generator(5)
  private parentElement: HTMLElement
  private pallet: Pallet = {}
  private palDef = new PalletDef()
  private boxDef = new BoxDef()

  private figureBuilder: FigureBuilder
  private options: PatternCanvasOptions
  private shadow = new fabric.Shadow({ color: consts.shadowCol, blur: 30, offsetX: 25, offsetY: 25 })
  private moving = false
  private snapper: Snap
  private conflicts: fabric.Object[] = []
  private gripperIssues: fabric.Object[] = []
  private warnings: fabric.Object[] = []
  private rotationIssues: fabric.Object[] = []
  private warningsRotation: fabric.Object[] = []
  private animationLock: boolean = false
  private isGroupActive: boolean = false
  private highlighted: fabric.Object[] = []
  private isCustomSolution: boolean = false
  private programSettings: ProgramSettings
  private resizeObserver: ResizeObserver | null = null

  constructor (parentID: string, elementID: string, box: Box, pallet: Pallet, programSettings: ProgramSettings,
               options?: PatternCanvasOptions) {
    // create fabric canvas in html element
    // both parent and child elements are needed
    const parent = document.getElementById(parentID)
    if (!parent) {
      console.log(`[ERROR] html element '${ parentID }' not found`)
    }
    let element = document.getElementById(elementID)
    if (!element) {
      console.log(`[ERROR] html element '${ elementID }' not found`)
      element = document.createElement(elementID)
      parent!.append(element)
    }
    while (element.firstChild) {
      element.firstChild.remove()
    }
    this.canvas = new fabric.Canvas(elementID)
    this.parentElement = parent!

    this.isCustomSolution = 'CUSTOM_SOLUTION' === programSettings.robotType
    this.programSettings = programSettings
    this._palletTopOffset = (this.isCustomSolution) ? 10 : this._palletTopOffset

    // graphic box properties
    this.boxDef.labelPos = programSettings.labelPosition!

    this.boxDef.width = programSettings.boxOrientationOnConveyor ===
    ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING ? box.length! : box.width!

    this.boxDef.height = programSettings.boxOrientationOnConveyor ===
    ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING ? box.width! : box.length!

    this.boxDef.actualHeight = box.height!

    this.boxDef.widthMargin = (programSettings.boxOrientationOnConveyor ===
    ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING ? box.lengthMargin : box.widthMargin) || 0

    this.boxDef.heightMargin = (programSettings.boxOrientationOnConveyor ===
    ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING ? box.widthMargin : box.lengthMargin) || 0

    this.pallet = pallet

    this.figureBuilder = new FigureBuilder()
    this.options = options ? options : defaultOptions
    this.palDef = getPalletDef(pallet)
    this.snapper = new Snap(this.canvas, this.boxDef, this.palDef, this._palletTopOffset)
  }

  onnewbox: (p: BoxPosition) => void = () => {
    // direct handler assignment because of performance
    // alternative use this.emit('event', ...) from events.EventEmitter
  }

  onmove: (p: BoxPosition) => void = () => {
    // direct handler assignment because of performance
    // alternative use this.emit('event', ...) from events.EventEmitter
  }

  onmodified: (p: BoxPosition) => void = () => {
    //
  }

  onselected: (id: number) => void = () => {
    //
  }

  ondelete: (id: number) => void = () => {
    //
  }

  async init () {
    await this.timeout(20)
    this.setupView()
    const palletReady = this.drawPalletBase()
    if (this.options.mode === 'full') {
      this.drawTemplateBoxes()
    }
    this.snapper.init()
    this.initResizeObserver()
    await palletReady
    this.render()
  }

  // - update existing box positions by id,
  // - delete boxes with id no longer actual,
  // - add new boxes for new positions (id = 0)
  updatePositions (positions: BoxPosition[], clear = true): void {
    const updated: number[] = []
    const removeCollection: fabric.Object[] = []

    // update boxes if they are in positions list
    this.canvas.getObjects().filter(obj => obj.name === 'box').forEach(box => {
      const newPos = positions.find(p => p.id === box.data.id)
      if (!newPos) {
        if (clear) {
          removeCollection.push(box)
        }
        return
      }
      const bp = this.getPositionOnPallet(newPos.x!, newPos.y!, this.palDef.displayRotation)
      const rotation = newPos.rotation != null ? newPos.rotation : 0
      box.left = bp.x
      box.top = bp.y
      box.rotate((360 - rotation + this.palDef.displayRotation) % 360)
      box.dirty = true
      box.setCoords()
      updated.push(newPos.id!)
    })
    // remove boxes that have no position anymore
    this.canvas.remove(...removeCollection)

    // add new boxes for new positions
    const newPositions = positions.filter(p => !updated.includes(p.id!))
    newPositions.forEach(p => {
      const bp = this.getPositionOnPallet(p.x!, p.y!, this.palDef.displayRotation)
      const point = {
        x: bp.x,
        y: bp.y,
        rotation: p.rotation
      }
      if (p.id && p.id > 0) {
        // set existing position's id to the new box
        this.addObjectBox(point.x, point.y, this.boxDef.width, this.boxDef.height, point.rotation!, 'box', true, p.id)
      } else {
        // update back id to position object from the new box
        p.id = this.addObjectBox(point.x, point.y, this.boxDef.width, this.boxDef.height, point.rotation!, 'box', false)
      }
    })
    if (this.options.mode !== 'full') {
      this.lockMovement(true)
    }
    this.canvas.renderAll()
  }

  updateOrder (positions: BoxPosition[], groups: number[][]): void {
    if (this.isCustomSolution) {
      return // do not display order numbers for custom solution
    }

    this.canvas.getObjects('group').filter(obj => obj.name === 'box').forEach(box => {
      const [ index, groupIndex ] = getRobotMovementNumber(box.data.id, positions, groups)
      const children = (box as fabric.Group).getObjects('text')
      const label = (children.find(child => child.name === 'label') as fabric.Text | undefined)
      if (label) {
        if (this.showDevelopmentLabels()) {
          this.setBoxLabelText(label, `${ (index >= 0 && !this.isCustomSolution) ? (index + '.' + box.data.id) : box.data.id }`)
        } else {
          this.setBoxLabelText(label, `${ index >= 0 ? index : '' }`)
        }

        label.set('angle', -box.angle! % 360)
        label.dirty = true
      }
      this.canvas.bringToFront(box)
    })
  }

  bringToFrontGroups (): void {
    this.updateTopObjects()
  }

  updateGroupLabels (boxGroupData: BoxGroupData): void {
    // console.log('view.updateGroupLabels')
    this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box')
      .forEach(box => {
        // box.data.group = undefined

        let text = ''
        const groupTextCollection = (box as fabric.Group)
          .getObjects('group')
          .filter(p => p.name === 'group')

        if (groupTextCollection && groupTextCollection.length > 0) {
          const group = groupTextCollection[0] as fabric.Group
          group.set('angle', -box.angle! % 360)
          group.dirty = true

          // const groupData = groups.find(g => g.includes(Number(box.data.id))) || []
          // const sorted = sortGroupByPlace(positions, groupData)
          // const indexInGroup = sorted.findIndex(p => p === box.data.id)

          // Need a map/object structure:
          // id: {group_number, pick_position_in_group, place_position_in_group]

          const dataItem = (boxGroupData[box.data.id] || {})
          const pickOrderInGroup = dataItem.pick
          const placeOrderInGroup = dataItem.place

          text = (placeOrderInGroup > 0 ? placeOrderInGroup : '').toString()

          if (this.showDevelopmentLabels() && placeOrderInGroup > 0) {
            text += ' ' + (pickOrderInGroup > 0 ? pickOrderInGroup.toString() : '_')
          }

          if (!this.showDevelopmentLabels() && pickOrderInGroup === 0) {
            text = '' // hide group label if place both boxes together
          }

          const gt = group.getObjects('text').find(p => p.name === 'group-text')
          if (gt) {
            this.setGroupLabelText(gt as fabric.Text, text)
          }
        }
      })
  }

  updateGroups (groups: Array<Array<{ x: number, y: number, pusher?: boolean, doublePlace?: boolean }>>): void {
    // clean groups
    this.canvas.remove(...this.canvas.getObjects().filter(obj => obj.name === 'pushergroup'))
    if (!groups || groups.length < 1) {
      return
    }

    // draw group objects
    // console.log('[DEBUG] draw pusher groups', JSON.stringify(groups))
    const objects = groups.map(points => {
      const borders = this.figureBuilder.makeGroupBorders(points)
      borders.forEach(border => {
        const pos = this.getPositionOnPallet(border.left!, border.top!, this.palDef.displayRotation)
        border.left = pos.x
        border.top = pos.y
      })
      return borders
    })

    const collection = objects.flat()
    this.canvas.add(...collection)
    collection.forEach(p => {
      this.canvas.bringToFront(p)
    })
    this.canvas.renderAll()
  }

  clear (): void {
    this.snapper.clear()
    this.canvas.remove(...this.canvas.getObjects().filter(p => p.name === 'box' || p.name === 'multigroup' || p.name === 'pushergroup' || p.name === 'gripper-border'))
  }

  clearSelection (): void {
    this.canvas.discardActiveObject()
    this.canvas.renderAll()
    this.isGroupActive = false
  }

  selectCollection (ids: number[]): void {
    this.canvas.getObjects()
      .filter(obj => obj.name === 'box' && obj.data && ids.includes(obj.data.id))
      .forEach(box => {
        (box as fabric.Group).getObjects().filter(p => p.name === 'blur')
          .forEach(q => q.set({ opacity: 0.15 }))
      })
    this.canvas.renderAll()
  }

  deselectCollection (ids: number[]): void {
    let callback = (id: number): boolean => {
      return ids.includes(id)
    }
    if (!ids || ids.length === 0) {
      callback = (id: number): boolean => {
        return true
      }
    }
    this.canvas.getObjects()
      .filter(obj => obj.name === 'box' && obj.data && callback(obj.data.id))
      .forEach(box => {
        (box as fabric.Group).getObjects().filter(p => p.name === 'blur')
          .forEach(q => q.set({ opacity: 0 }))
      })
    this.canvas.renderAll()
  }

  async rotate180 (id: number) {
    const obj = this.canvas.getObjects().find(o => o.name === 'box' && o.data.id === id)
    if (!obj) {
      return
    }
    const duration = 500
    const startAngle = obj.angle!
    this.canvas.renderAll.bind(this.canvas)
    obj.animate('angle', (startAngle + 180).toString(), {
      onChange: this.canvas.renderAll.bind(this.canvas),
      duration: duration
    })

    const sleep = (ms: number) => {
      return new Promise(resolve => {
        setTimeout(resolve, ms)
      })
    }

    await sleep(duration * 1.2)

    // obj.rotate((startAngle + 180) % 360)
    this.figureBuilder.makeTextVertical(obj as fabric.Group)
    this.canvas.bringToFront(obj)
    this.updateTopObjects()
  }

  rotateActive (angle: number): number {
    let id = -1
    if (this.animationLock) {
      return id
    }
    const obj = this.canvas.getActiveObject()
    if (obj && obj.name === 'box') {
      id = obj.data.id
      // rotate 90 degrees clockwise
      obj.rotate((obj.angle! + angle) % 360)
      this.figureBuilder.makeTextVertical(obj as fabric.Group)
      this.canvas.bringToFront(obj)
      this.updateTopObjects()
    }
    return id
  }

  deleteActive (boxId?: number): number {
    let id = -1
    let obj: fabric.Object | undefined = this.canvas.getActiveObject()
    if ((!obj || obj.name !== 'box') && boxId && boxId > 0) {
      obj = this.canvas.getObjects().find(o => o.name === 'box' && o.data.id === boxId)
    }
    if (obj && obj.name === 'box') {
      id = obj.data.id
      this.canvas.remove(obj)
    }
    return id
  }

  setSnapMode (active: boolean) {
    this.snapper.setActive(active)
  }

  resize (w: number, h: number) {
    this.canvas.setWidth(w)
    this.canvas.setHeight(h)
    this.canvas.renderAll()
  }

  setupView () {
    this.palDef = getPalletDef(this.pallet)

    // console.log('[DEBUG] parent', parent)
    const w = this.parentElement.offsetWidth
    const h = this.parentElement.offsetHeight
    const zoom = this.getZoom(w, h, this.pallet.length! + this.palDef.overhangL + this.palDef.overhangR,
      this.pallet.width! + this.palDef.overhangT + this.palDef.overhangB)
    // console.log('[DEBUG] w, h, zoom,', w, h, zoom)
    this.canvas.clear()
    this.canvas.hoverCursor = 'default'
    this.canvas.setZoom(zoom)
    this.canvas.setWidth(w)
    this.canvas.setHeight(h)
    this.canvas.preserveObjectStacking = true
    fabric.Group.prototype.hasControls = false
    fabric.Group.prototype.borderColor = consts.selectedBorder
    fabric.Group.prototype.padding = 5

    this.canvas.selection = false

    this.canvas.on('mouse:up', () => this.clearTextSelection())
    this.canvas.on('object:moving', this.movingHandler)
    this.canvas.on('object:modified', this.modifiedHandler)
    this.canvas.on('selection:created', this.selectionHandler)
    this.canvas.on('selection:updated', this.selectionHandler)
    this.canvas.on('selection:cleared', this.selectionHandler)
  }

  render () {
    this.canvas.renderAll()
  }

  drawPalletBase (): Promise<void> {
    const pallet = this.pallet
    const palDef = this.palDef
    const palImgFilename = this.getPalImgFilename(pallet.palletType!)

    return new Promise(resolve => {
      fabric.loadSVGFromURL(`img/${ palImgFilename }`, (objects, options) => {
        const shapes = []
        const shape = fabric.util.groupSVGElements(objects, options)
        shape.scaleX = (shape.width) ? (palDef.width / shape.width) : 1
        shape.scaleY = (shape.height) ? (palDef.height / shape.height) : 1
        shape.set({ left: 0, top: 0, name: 'background.pallet' })
        shape.setCoords()
        shapes.push(shape)

        if (palDef.overhangL + palDef.overhangR + palDef.overhangT + palDef.overhangB > 0) {
          const overhang = this.figureBuilder.makePalletOverhang(
            palDef.width + palDef.overhangL + palDef.overhangR,
            palDef.height + palDef.overhangT + palDef.overhangB
          )
          overhang.set({ left: -palDef.overhangL, top: -palDef.overhangT })
          shapes.push(overhang)
        }

        const groupOpt = {
          left: palDef.topLeftX,
          top: palDef.topLeftY + this._palletTopOffset,
          name: 'background',
          hasControls: false,
          lockMovementX: false,
          lockMovementY: false,
          selectable: false
        }
        const group = new fabric.Group(shapes, groupOpt)

        if (this.options.side === 'R') {
          this.drawRobotImage(group.top! - this._palletTopOffset - 20, group.left! + group.width!)
        }

        this.canvas.add(group)
        this.canvas.moveTo(group, consts.zforPal)
        if (this.options.side === 'L') {
          this.drawRobotImage(group.top! + group.height! + 20, group.left! + group.width!)
        }
        if (this.isCustomSolution) {
          this.drawReferenceFrame()
        }
        if (this.palDef.displayRotation > 0) {
          this.rotatePallet(this.palDef.displayRotation)
        }
        resolve()
      })
    })
  }

  rotatePallet (angle: number) {
    console.log('rotatePallet: ', angle, this.palDef.displayRotation)
    const pallet = this.canvas.getObjects().find(o => o.name === 'background')
    angle = ((angle % 360) + 360) % 360 // convert to clockwise
    if (pallet) {
      const a = (pallet.angle! + angle) % 360
      pallet.rotate(a)

      // correct position
      const diff = pallet.width! * 0.5 - pallet.height! * 0.5
      const initialCenterX = this.palDef.topLeftX + pallet.width! * 0.5
      const initialCenterY = this.palDef.topLeftY + this._palletTopOffset + pallet.height! * 0.5
      const left = initialCenterX - (utils.vectorX(a) * pallet.width! * 0.5) + (utils.vectorY(a) * pallet.height! * 0.5) - (utils.projY(a) * diff)
      const top = initialCenterY - (utils.vectorX(a) * pallet.height! * 0.5) - (utils.vectorY(a) * pallet.width! * 0.5) + (utils.projY(a) * diff)
      pallet.set({ left: left, top: top })
      pallet.setCoords()
      const newCenter = pallet.getCenterPoint()

      // rotate reference frame
      const ref = this.canvas.getObjects().find(o => o.name === 'reference')
      if (ref) {
        const ar = (ref.angle! + angle) % 360
        ref.rotate(ar)
        // correct position depending on pallet
        ref.set({ left: newCenter.x, top: newCenter.y })
        ref.setCoords()
      }

      // rotate boxes
      this.canvas.getObjects().filter(obj => obj.name === 'box').forEach(box => {
        const ab = (box.angle! + angle) % 360
        const point = box.getCenterPoint()
        const pos = this.getBoxPosition(point.x, point.y, this.palDef.displayRotation)
        box.rotate(ab)

        const palletPos = this.getPositionOnPallet(pos.x, pos.y, a)
        box.set({ left: palletPos.x, top: palletPos.y })
        box.setCoords()
      })

      // update template boxes
      const templateBoxes = this.canvas.getObjects().filter(obj => obj.name && obj.name.startsWith(consts.templatePrefix))
      this.canvas.remove(...templateBoxes)
      this.drawTemplateBoxes(a)

      // correct zoom
      const zoom = (a === 90 || a === 270)
        ? this.getZoom(this.canvas.getWidth(), this.canvas.getHeight(),
          this.pallet.width! + this.palDef.overhangT + this.palDef.overhangB,
          this.pallet.length! + this.palDef.overhangL + this.palDef.overhangR)
        : this.getZoom(this.canvas.getWidth(), this.canvas.getHeight(),
          this.pallet.length! + this.palDef.overhangL + this.palDef.overhangR,
          this.pallet.width! + this.palDef.overhangT + this.palDef.overhangB)
      this.canvas.setZoom(zoom)

      this.palDef.displayRotation = a
      this.updateTopObjects()
      this.canvas.renderAll()
      return a
    }
    return -1
  }

  drawReferenceFrame(): void {
    const palDef = this.palDef
    const pallet = this.canvas.getObjects().find(o => o.name === 'background')
    if (!pallet) {
      return // no pallet, no reference frame
    }
    const group = this.figureBuilder.makeReferenceFrame(
      palDef.width + 6, palDef.height + 6,
      pallet.getCenterPoint().x, pallet.getCenterPoint().y
    )
    this.canvas.add(group)
    this.canvas.moveTo(group, consts.zforPal)
  }

  drawRobotImage (top: number, left: number) {
    if (this.isCustomSolution) {
      return // do not display robot for custom solution
    }
    fabric.Image.fromURL('/img/robot-from-above.png', (robotArmImg: Image) => {
      const img = robotArmImg.set({
        left: left,
        top: top,
        originX: 'right',
        originY: 'top',
        width: 306,
        height: 146,
        name: 'robot-arm-image',
        hasControls: false,
        lockMovementX: false,
        lockMovementY: false,
        selectable: false,
      })
      img.scale(0.8)
      this.canvas.add(img)
    })
  }

  removeConflicts () {
    this.conflicts.forEach(c => c.set('opacity', 0))
    this.conflicts.length = 0
  }

  drawConflicts (ids: number[]) {
    this.removeConflicts()
    if (!ids || ids.length < 1) {
      return
    }
    // @ts-ignore
    const boxes: fabric.Group[] = this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box' && ids.includes(obj.data.id))
    this.conflicts = boxes.map(b => b.getObjects().filter(o => o.name === 'cross')).flat()
    this.conflicts.forEach(obj => obj.set('opacity', 1))
  }

  removeGripperIssues () {
    this.gripperIssues.forEach(c => c.set('opacity', 0))
    this.gripperIssues.length = 0
    this.canvas.renderAll()
  }

  drawGripperIssues (ids: number[]) {
    this.removeGripperIssues()
    if (!ids || ids.length < 1) {
      return
    }
    // @ts-ignore
    const boxes: fabric.Group[] = this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box' && ids.includes(obj.data.id))
    this.gripperIssues = boxes.map(b => b.getObjects().filter(o => o.name === 'error')).flat()
    this.gripperIssues.forEach(obj => obj.set('opacity', 1))
    this.canvas.renderAll()
  }

  removeWarnings () {
    this.warnings.forEach(c => c.set('opacity', 0))
    this.warnings.length = 0
    this.canvas.renderAll()
  }

  drawWarnings (ids: number[]) {
    this.removeWarnings()
    if (!ids || ids.length < 1) {
      return
    }
    // @ts-ignore
    const boxes: fabric.Group[] = this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box' && ids.includes(obj.data.id))
    this.warnings = boxes.map(b => b.getObjects().filter(o => o.name === 'warning')).flat()
    this.warnings.forEach(obj => obj.set('opacity', 1))
    this.canvas.renderAll()
  }

  lockMovement (value: boolean) {
    // console.log('[DEBUG] lock boxes')
    this.canvas.getObjects().filter(obj => obj.name === 'box').forEach(box => {
      box.lockMovementX = value
      box.lockMovementY = value
    })
  }

  isSelected (id: number): boolean {
    const obj = this.canvas.getActiveObject()
    if (obj && obj.name === 'box') {
      return id === obj.data.id
    }
    return false
  }

  showGripperBorder (points: Array<{ x: number, y: number }>, pX?: number, pY?: number, rotation: number = 0): void {
    this.canvas.remove(...this.canvas.getObjects().filter(obj => obj.name === 'gripper-border'))
    if (points.length < 1) {
      return
    }
    // console.log('[DEBUG] draw gripper border: ', points, this.canvas.getZoom())
    const isOverlay = pX != null && pY != null
    const borderPoints = isOverlay
      ? points.map(point => this.getPositionOnPallet(point.x, point.y, this.palDef.displayRotation))
      : points
    const border = this.figureBuilder.makeGripperBorders(borderPoints, false)
    // adjustment to pallet
    const tempX = pX != null ? pX : border.left!
    const tempY = pY != null ? pY : border.top!
    const pos = this.getPositionOnPallet(tempX, tempY, this.palDef.displayRotation)
    if (!isOverlay) {
      border.left = pos.x
      border.top = pos.y
      border.setCoords()
    }
    this.canvas.add(border)
    this.canvas.bringToFront(border)
    if (isOverlay) {
      const width = Math.max(...points.map(point => point.x)) - Math.min(...points.map(point => point.x))
      const height = Math.max(...points.map(point => point.y)) - Math.min(...points.map(point => point.y))
      const tcpAndReferenceFrame = this.figureBuilder.makeGripperTcpReferenceFrame(pos.x, pos.y, width, height, rotation - this.palDef.displayRotation)
      this.canvas.add(...tcpAndReferenceFrame)
      tcpAndReferenceFrame.forEach(obj => this.canvas.bringToFront(obj))
    }
    this.canvas.renderAll()
  }

  removeRotationIssues () {
    this.rotationIssues.forEach(c => c.set('opacity', 0))
    this.rotationIssues.length = 0
    this.canvas.renderAll()
  }

  drawRotationIssues (ids: number[]) {
    this.removeRotationIssues()
    if (!ids || ids.length < 1) {
      return
    }
    // @ts-ignore
    const boxes: fabric.Group[] = this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box' && ids.includes(obj.data.id))
    this.rotationIssues = boxes.map(b => b.getObjects().filter(o => o.name === 'rotation')).flat()
    this.rotationIssues.forEach(obj => obj.set('opacity', 1))
    this.canvas.renderAll()
  }

  removeRotationWarnings() {
    this.warningsRotation.forEach(c => c.set('opacity', 0))
    this.warningsRotation.length = 0
    this.canvas.renderAll()
  }

  drawRotationWarnings(ids: number[]) {
    this.removeRotationWarnings()
    if (!ids || ids.length < 1) {
      return
    }
    // @ts-ignore
    const boxes: fabric.Group[] = this.canvas.getObjects('group')
      .filter(obj => obj.name === 'box' && ids.includes(obj.data.id))
    this.warningsRotation = boxes.map(b => b.getObjects().filter(o => o.name === 'warningArrow')).flat()
    this.warningsRotation.forEach(obj => obj.set('opacity', 1))
    this.canvas.renderAll()
  }

  showRulers (id: number) {
    const box = this.canvas.getObjects().filter(obj => obj.name === 'box' && id === obj.data.id)
    if (box) {
      this.snapper.drawRulers(box[0], true)
    }
  }

  highlightBox (id: number) {
    const box = this.canvas.getObjects('group').find(obj => obj.name === 'box' && id === obj.data.id)
    if (box) {
      (box as fabric.Group).getObjects().filter(o => o.name === 'highlight')
        .forEach(q => q.set({ opacity: 0.5 }))
      this.highlighted.push(box)
    }
    this.canvas.renderAll()
  }

  removeHighlightedBox () {
    this.highlighted.forEach(box => {
      (box as fabric.Group).getObjects().filter(o => o.name === 'highlight')
        .forEach(q => q.set({ opacity: 0 }))
    })
    this.highlighted.length = 0
    this.canvas.renderAll()
  }

  getPalletOffset(rotation: number, skipTopOffset: boolean = false) {
    const topOffset = (skipTopOffset) ? 0 : this._palletTopOffset
    switch (rotation) {
      case 90:
        return {
          x: this.palDef.topLeftX + this.palDef.overhangB + this.palDef.height,
          y: this.palDef.topLeftY + this.palDef.overhangL + topOffset
        }
      case 180:
        return {
          x: this.palDef.topLeftX + this.palDef.overhangR + this.palDef.width,
          y: this.palDef.topLeftY + this.palDef.overhangB + this.palDef.height + topOffset
        }
      case 270:
        return {
          x: this.palDef.topLeftX + this.palDef.overhangT,
          y: this.palDef.topLeftY + this.palDef.overhangR + this.palDef.width + topOffset
        }
      case 0:
      default:
        return {
          x: this.palDef.topLeftX + this.palDef.overhangL,
          y: this.palDef.topLeftY + this.palDef.overhangT + topOffset
        }
    }
  }

  getBoxPosition(centerX: number, centerY: number, palletRotation: number) {
    const offset = this.getPalletOffset(palletRotation, true)
    let x
    let y
    switch (palletRotation) {
      case 90:
        x = Math.floor(centerY - offset.y + 0.5) - this._palletTopOffset
        y = Math.floor((offset.x + 0.5) - centerX)
        break
      case 180:
        x = Math.floor((offset.x + 0.5) - centerX)
        y = Math.floor((offset.y + 0.5 + this._palletTopOffset) - centerY)
        break
      case 270:
        x = Math.floor((offset.y + 0.5 + this._palletTopOffset) - centerY)
        y = Math.floor(centerX - offset.x + 0.5)
        break
      case 0:
      default:
        x = Math.floor(centerX - offset.x + 0.5)
        y = Math.floor(centerY - offset.y + 0.5) - this._palletTopOffset
    }
    x = (this.isCustomSolution) ? this.pallet.length! - x : x
    // console.log('getBoxPosition: ', x, y, JSON.stringify(offset), centerX, centerY, palletRotation, this._palletTopOffset)
    return { x: x, y: y }
  }

  getPositionOnPallet(x: number, y: number, palletRotation: number, convert: boolean = true) {
    const offset = this.getPalletOffset(palletRotation)
    let updatedX: number
    let updatedY: number
    const tempX = (this.isCustomSolution && convert) ? this.pallet.length! - x : x
    switch (palletRotation) {
      case 90:
        updatedX = -y + offset.x
        updatedY = tempX + offset.y
        break
      case 180:
        updatedX = -tempX + offset.x
        updatedY = -y + offset.y
        break
      case 270:
        updatedX = y + offset.x
        updatedY = -tempX + offset.y
        break
      case 0:
      default:
        updatedX = tempX + offset.x
        updatedY = y + offset.y
    }
    // console.log('getPositionOnPallet: ', updatedX, updatedY, JSON.stringify(offset), x, y, palletRotation)
    return { x: updatedX, y: updatedY }
  }

  updateSequences (sequences: Sequence[]): void {
    if (!this.isCustomSolution) {
      return
    }

    this.canvas.getObjects('group').filter(obj => obj.name === 'box').forEach(box => {
      const sequence = findSequenceByBoxId(box.data.id, sequences)
      box.lockMovementX = sequence != null
      box.lockMovementY = sequence != null
      let sequenceId
      let pickId
      let pickRotation = 0
      if (sequence) {
        sequenceId = sequence.id
        const pick = sequence.pick!.find(p => (p.boxesGroup || []).includes(box.data.id))
        pickId = pick ? pick.id : undefined
        pickRotation = pick && pick.zRot != null ? pick.zRot : 0
      }
      let customLabelPosition = LabelPositionEnum.IGNORE
      if (sequence) {
        const placeRotation = sequence.place && sequence.place[0] && sequence.place[0].zRot != null ? sequence.place[0].zRot : 0
        const boxRotation = this.getPosition(box).rotation || 0
        customLabelPosition = calculateCustomLabelPosition(
          this.programSettings.labelPosition,
          this.programSettings.boxOrientationOnConveyor,
          this.programSettings.conveyorConfiguration,
          boxRotation,
          pickRotation,
          placeRotation,
          getCustomInfeedRotation(sequence)
        )
      }
      this.figureBuilder.updateBoxLabel(
        box as fabric.Group,
        this.boxDef.width,
        this.boxDef.height,
        customLabelPosition,
        sequence != null && customLabelPosition !== LabelPositionEnum.IGNORE
      )
      const children = (box as fabric.Group).getObjects('text')
      const label = (children.find(child => child.name === 'label') as fabric.Text | undefined)
      if (label) {
        if (this.showDevelopmentLabels()) {
          this.setBoxLabelText(label, `${ sequenceId != null ? sequenceId + '_' + pickId + '.' + box.data.id : box.data.id}` )
        } else {
          this.setBoxLabelText(label, `${ sequenceId != null ? sequenceId + '_' + pickId : '' }`)
        }
        label.set('angle', -box.angle! % 360)
        label.dirty = true
      }
      this.canvas.bringToFront(box)
    })
    this.updateTopObjects()
    const canvas = this.canvas as any
    if (canvas.requestRenderAll) {
      canvas.requestRenderAll()
    } else if (canvas.renderAll) {
      canvas.renderAll()
    }
  }

  initResizeObserver (): void {
    if (!this.parentElement) {
      return
    }
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        const {width, height} = entry.contentRect
        this.adjustZoomAndSize (width, height)
      }
    })
    this.resizeObserver.observe(this.parentElement)
  }

  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  // =================================================================================================================
  //
  //  PRIVATE METHODS
  //
  // =================================================================================================================

  private movingHandler = (options: any) => {
    const box: fabric.Group = options.target
    // check if it's a new box dragging in
    if (box.name && box.name.startsWith(consts.templatePrefix)) {
      this.convertTemplateToStandardBox(box, this.boxDef)
      box.data.newFromTemplate = true
      this.onnewbox(this.getPosition(box))
      this.drawTemplateBoxes()
    }
    // when start dragging, update snap points
    if (!this.moving) {
      this.moving = true
      this.snapper.updatePoints(box)
      this.canvas.bringToFront(box)

      // show box shadow depends on settings option
      // if (true || this.options.shadow) {
      box.setShadow(this.shadow)
      // }
    }
    box.setCoords()
    this.snapper.highlightDropzone(box)
    this.snapper.drawRulers(box)
    this.onmove(this.getPosition(box))
    // this.isGroupActive = false
  }

  private modifiedHandler = (options: any) => {
    // object moving stopped
    this.moving = false
    options.target.setShadow('false')
    this.canvas.discardActiveObject()
    this.snapper.removeRulers()
    const newFromTemplate = options.target.data && options.target.data.newFromTemplate
    if (this.isReturnedToTemplates(options.target) && !newFromTemplate) {
      const id = this.deleteActive(options.target.data.id)
      this.snapper.clear()
      this.ondelete(id)
      this.updateTopObjects()
      return
    }
    if (newFromTemplate) {
      delete options.target.data.newFromTemplate
    }
    const snapped = this.snapper.dropToSnap(options.target)
    const position = this.getPosition(options.target)
    if (snapped) {
      this.onmove(position)
    }
    this.snapper.clear()
    this.onmodified(position)
    this.updateTopObjects()
    // this.isGroupActive = false
  }

  private isReturnedToTemplates (box: fabric.Object): boolean {
    return box.name === 'box' && box.getCenterPoint().x < this.palDef.topLeftX - 20
  }

  private selectionHandler = (options: any) => {
    if (options.selected == null) {
      this.canvas.remove(...this.canvas.getObjects().filter(p => p.name === 'gripper-border'))
      this.onselected(-1)
      this.snapper.removeRulers()
    }
  }

  private mouseupHandler = (event: fabric.IEvent) => {
    // this.isGroupActive = false
    if (event.target && event.target.data) {
      const active = this.canvas.getActiveObject()
      if (active && active.name === 'box' && active.data.id === event.target.data.id) {
        // console.log('[DEBUG] mouse down -> on selected')
        // console.log('[DEBUG] selection alert', event.target.data.id)
        this.onselected(event.target.data.id)
        this.snapper.updatePoints(active) // triggers creation of snap points
      }
    }
  }

  private updateTopObjects () {
    this.canvas.getObjects().filter(p => p.name === 'pushergroup')
      .forEach(g => {
        this.canvas.bringToFront(g)
      })
    this.canvas.getObjects().filter(p => p.name === 'gripper-border')
      .forEach(g => {
        this.canvas.bringToFront(g)
      })
  }

  private drawTemplateBoxes (pRotation?: number) {
    const palletRotation = (pRotation !== undefined) ? pRotation : this.palDef.displayRotation
    const templateScale = 0.8 * Math.min(
      this.palDef.topLeftX / this.boxDef.width,
      this.palDef.topLeftX / this.boxDef.height
    )
    const tmplWidth = this.boxDef.width * templateScale
    const tmplHeight = this.boxDef.height * templateScale
    if (!this.isCustomSolution && this.boxDef.labelPos !== LabelPositionEnum.IGNORE) {
      const extraMarginTop = utils.projY(palletRotation) * (tmplWidth * 0.5 - tmplHeight * 0.5)
      const marginTop = 40
      const marginLeft = 10
      const x = marginLeft + Math.max(tmplWidth, tmplHeight) / 2

      const params = [
        { rotation: (0 - palletRotation) % 360, suff: 'boxh', x: x, y: this._palletTopOffset + marginTop + extraMarginTop + tmplHeight / 2 },
        {
          rotation: (90 - palletRotation) % 360,
          suff: 'boxv',
          x: x,
          y: ((90 - palletRotation) % 180 !== 0)
            ? this._palletTopOffset + marginTop * 2 + tmplHeight + tmplWidth * 0.5
            : this._palletTopOffset + marginTop * 2 + tmplWidth + tmplHeight * 0.5
        },
        {
          rotation: (180 - palletRotation) % 360,
          suff: 'boxv2',
          x: x,
          y: ((180 - palletRotation) % 180 !== 0)
            ? this._palletTopOffset + marginTop * 3 + tmplHeight + tmplWidth * 1.5
            : this._palletTopOffset + marginTop * 3 + tmplWidth + tmplHeight * 1.5
        },
        {
          rotation: (270 - palletRotation) % 360,
          suff: 'boxh2',
          x: x,
          y: ((270 - palletRotation) % 180 !== 0)
            ? this._palletTopOffset + marginTop * 4 + tmplHeight * 2 + tmplWidth * 1.5
            : this._palletTopOffset + marginTop * 4 + tmplWidth * 2 + tmplHeight * 1.5
        }
      ]
      for (const p of params) {
        const templateName = consts.templatePrefix + p.suff
        const shape = this.canvas.getObjects().find(o => o.name === templateName)
        if (!shape) {
          this.addObjectBox(p.x, p.y, tmplWidth, tmplHeight, p.rotation, templateName, false)
        }
      }
      return
    }

    const customMarginTop = 60
    const customMarginLeft = 10
    const customX = customMarginLeft + Math.max(tmplWidth, tmplHeight) / 2
    const customFirstRotation: number = 0
    const customSecondRotation: number = 90
    const customFirstDisplayHeight = this.getTemplateDisplayHeight(customFirstRotation, palletRotation, tmplWidth, tmplHeight)
    const customSecondDisplayHeight = this.getTemplateDisplayHeight(customSecondRotation, palletRotation, tmplWidth, tmplHeight)

    const customParams = [
      {
        rotation: (customFirstRotation - palletRotation) % 360,
        suff: customFirstRotation === 0 ? 'boxh' : 'boxv',
        x: customX,
        y: this._palletTopOffset + customMarginTop + customFirstDisplayHeight / 2
      },
      {
        rotation: (customSecondRotation - palletRotation) % 360,
        suff: customSecondRotation === 0 ? 'boxh' : 'boxv',
        x: customX,
        y: this._palletTopOffset + customMarginTop * 2 + customFirstDisplayHeight + customSecondDisplayHeight / 2
      }
    ]
    for (const p of customParams) {
      const templateName = consts.templatePrefix + p.suff
      const shape = this.canvas.getObjects().find(o => o.name === templateName)
      if (!shape) {
        // console.log('[DEBUG] Adding new template box: ' + templateName)
        this.addObjectBox(p.x, p.y, tmplWidth, tmplHeight, p.rotation, templateName, false)
      }
    }
  }

  private getTemplateDisplayHeight (boxRotation: number, palletRotation: number, width: number, height: number): number {
    const relativeRotation = ((boxRotation - palletRotation) % 360 + 360) % 360
    return relativeRotation % 180 !== 0 ? width : height
  }

  private addObjectBox (x: number, y: number, width: number, height: number, rotation: number,
                        templateName: string, withIdx: boolean, givenIndex = -1): number {
    // console.log('addObjectBox: ', x, y, width, height, rotation, templateName)
    this.clearTextSelection()
    const label = (withIdx && !this.isCustomSolution) ? givenIndex : undefined
    // const gid = (groupId !== -1) ? groupId : undefined
    const id = (withIdx) ? givenIndex : this.boxId.next()

    const showBoxLabel = this.boxDef.labelPos !== LabelPositionEnum.IGNORE &&
      (this.isCustomSolution ? !templateName.startsWith(consts.templatePrefix) : true)
    const lgroup = this.figureBuilder.createObjectBoxContents(width, height, this.boxDef, label, templateName === 'box', '', showBoxLabel)

    const customOptions = {
      left: x,
      top: y,
      name: templateName,
      // multiPickGroup: gid,
      centeredRotation: true,
      originX: 'center',
      originY: 'center',
      data: { id: id },
      hoverCursor: 'move'
    }
    const group = new fabric.Group(lgroup, customOptions)
    group.on('mouseup', this.mouseupHandler)
    // convert from pallet coordinate system (anti-clockwise rotation) to fabric.js angles (clockwise rotation).
    const angle = (360 - rotation + (templateName === 'box' ? this.palDef.displayRotation : 0)) % 360
    group.rotate(angle)
    this.canvas.add(group)
    this.canvas.bringToFront(group)
    this.updateTopObjects()
    return id
  }

  private showDevelopmentLabels (): boolean {
    return !!this.options.debugBoxIds || process.env.NODE_ENV === 'development'
  }

  private setBoxLabelText (label: fabric.Text, text: string) {
    label.set('text', text)
    const shortestSide = Math.min(this.boxDef.width, this.boxDef.height)
    const baseSize = Math.min(consts.boxLabel.fontSize, Math.floor(shortestSide * 0.18))
    const sizeByLength = text ? Math.floor(shortestSide * 0.62 / text.length * 1.35) : baseSize
    label.set('fontSize', Math.max(18, Math.min(baseSize, sizeByLength)))
  }

  private setGroupLabelText (label: fabric.Text, text: string) {
    label.set('text', text)
    const shortestSide = Math.min(this.boxDef.width, this.boxDef.height)
    const baseSize = Math.min(consts.boxLabel.fontSize * 0.8, Math.floor(shortestSide * 0.16))
    const sizeByLength = text ? Math.floor(shortestSide * 0.5 / text.length * 1.2) : baseSize
    label.set('fontSize', Math.max(16, Math.min(baseSize, sizeByLength)))
  }

  private getPosition (box: fabric.Object): BoxPosition {
    const point = box.getCenterPoint()
    const pos = this.getBoxPosition(point.x, point.y, this.palDef.displayRotation)
    // convert from fabric.js angles (clockwise rotation) to pallet coordinate system (anti-clockwise rotation)
    const rotation = (360 - box.angle! + this.palDef.displayRotation) % 360
    // console.log('PC.getPosition - rotation: ', rotation, box.angle, this.palDef.displayRotation)
    return { id: box.data.id, x: pos.x, y: pos.y, rotation: rotation }
  }

  // noinspection JSMethodCanBeStatic
  private getZoom (w: number, h: number, palletLength: number, palletWidth: number): number {
    const leftBoxPanelWidth = palletLength / 4
    // have to set margin-right 1.09
    const z1 = w / ((palletLength + leftBoxPanelWidth) * 1.02)
    const z2 = h / (palletWidth + this._palletTopOffset * 2) * 0.92
    const zoom = Math.min(z1, z2)
    return zoom
  }

  private convertTemplateToStandardBox (box: fabric.Group, boxDef: BoxDef) {
    if (box.name && box.name.startsWith(consts.templatePrefix)) {
      this.clearTextSelection()
      const idsOnPallet = this.canvas.getObjects().filter(p => p.name === 'box').map(b => b.data.id)
      box.data.id = Math.max(...idsOnPallet, 0) + 1
      box.name = 'box'

      // recreate the box contents with the standard box width and height
      box.remove(...box.getObjects())

      const lgroup = this.figureBuilder.createObjectBoxContents(
        boxDef.width,
        boxDef.height,
        boxDef,
        -1,
        true,
        '',
        !this.isCustomSolution && boxDef.labelPos !== LabelPositionEnum.IGNORE
      )
      lgroup.forEach(p => box.add(p))
      // @ts-ignore
      box._calcBounds()
    }
  }

  private clearTextSelection = () => {
    const selection = document.getSelection ? document.getSelection() : (window.getSelection ? window.getSelection() : null)
    if (selection) {
      selection.removeAllRanges()
    }
  }

  private timeout (ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private getPalImgFilename (palletType: string): string {
    switch (palletType) {
      case 'INDU':
        return 'uspalette.svg'
      case 'US':
        return 'napalette.svg'
      case 'JP':
        return 'jppalette.svg'
      case 'IL':
      case 'CHEM':
        return 'ilpalette.svg'
      case 'AU':
        return 'aupalette.svg'
      case 'EUR':
      default:
        return 'europalette.svg'
    }
  }

  private adjustZoomAndSize(width: number, height: number) {
    if (width === 0 || height === 0) {
      return
    }
    const zoom = this.getZoom(width, height, this.pallet.length! + this.palDef.overhangL + this.palDef.overhangR,
      this.pallet.width! + this.palDef.overhangT + this.palDef.overhangB)
    // console.log('PC.adjustZoom - width, height, zoom: ', width, height, zoom, this.options.side)
    this.canvas.setZoom(zoom)
    this.canvas.setWidth(width)
    this.canvas.setHeight(height)
    this.render()
  }
}
